import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { OVERVIEW_CONTENT } from '../content/overviewContent';
import {
  GUIDE_ACCENT,
  guideContainedButtonSx,
  guideOutlineButtonSx,
  guideTokens,
} from '../theme/guideTokens';

interface GuideHeroProps {
  onStartPhantom: () => void;
}

export const GuideHero: React.FC<GuideHeroProps> = ({ onStartPhantom }) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="overline"
        sx={{
          color: GUIDE_ACCENT,
          fontFamily: guideTokens.fontDisplay,
          fontWeight: 700,
          letterSpacing: '0.08em',
          display: 'block',
          mb: 1,
        }}
      >
        {OVERVIEW_CONTENT.eyebrow}
      </Typography>

      <Typography
        variant="h3"
        component="h1"
        sx={{
          fontFamily: guideTokens.fontDisplay,
          fontWeight: 800,
          fontSize: { xs: '1.75rem', sm: '2.1rem' },
          letterSpacing: '-0.03em',
          lineHeight: 1.15,
          mb: 1.25,
        }}
      >
        {OVERVIEW_CONTENT.title}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          fontFamily: guideTokens.fontBody,
          maxWidth: 560,
          lineHeight: 1.7,
          mb: 2.5,
        }}
      >
        {OVERVIEW_CONTENT.lead}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {[
          {
            key: 'receive',
            icon: <ArrowDownwardIcon sx={{ fontSize: 20, color: GUIDE_ACCENT }} />,
            ...OVERVIEW_CONTENT.receiveCard,
          },
          {
            key: 'send',
            icon: <ArrowUpwardIcon sx={{ fontSize: 20, color: GUIDE_ACCENT }} />,
            ...OVERVIEW_CONTENT.sendCard,
          },
        ].map((card) => (
          <Box
            key={card.key}
            sx={{
              p: 2,
              borderRadius: `${guideTokens.radiusCard}px`,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)',
            }}
          >
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.75 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (t) => guideTokens.accentSoft(t.palette.mode === 'dark'),
                }}
              >
                {card.icon}
              </Box>
              <Typography
                sx={{
                  fontFamily: guideTokens.fontDisplay,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                }}
              >
                {card.title}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: guideTokens.fontBody, lineHeight: 1.55 }}
            >
              {card.body}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          mb: 2.5,
          p: 2,
          borderRadius: `${guideTokens.radiusCard}px`,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          sx={{
            fontFamily: guideTokens.fontDisplay,
            fontWeight: 700,
            fontSize: '0.85rem',
            mb: 1,
          }}
        >
          What you’ll do
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          {OVERVIEW_CONTENT.checklist.map((item) => (
            <Typography
              component="li"
              key={item}
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.6, fontFamily: guideTokens.fontBody, lineHeight: 1.55 }}
            >
              {item}
            </Typography>
          ))}
        </Box>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button variant="contained" onClick={onStartPhantom} sx={guideContainedButtonSx}>
          Start with Phantom
        </Button>
        <Button
          component="a"
          href={OVERVIEW_CONTENT.rewardsUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          endIcon={<OpenInNewIcon />}
          sx={guideOutlineButtonSx}
        >
          View Rewards program
        </Button>
      </Stack>
    </Box>
  );
};
